/**
 * TranscriptViewer
 * Displays meeting transcript with speaker colors, timestamps, and search
 */

import React, { useState, useMemo } from 'react';
import { Search, User } from 'lucide-react';
import { Transcript } from '../../../types';
import { Input } from '../../ui/Input';
import styles from './TranscriptViewer.module.css';

export interface TranscriptViewerProps {
  transcript: Transcript;
}

export const TranscriptViewer: React.FC<TranscriptViewerProps> = ({ transcript }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSpeaker, setSelectedSpeaker] = useState<string>('');

  // Get unique speakers
  const speakers = useMemo(() => {
    const uniqueSpeakers = new Set(transcript.segments.map((s) => s.speaker));
    return Array.from(uniqueSpeakers);
  }, [transcript.segments]);

  // Assign colors to speakers
  const speakerColors = useMemo(() => {
    const colors = [
      '#6366f1', // indigo
      '#8b5cf6', // purple
      '#22d3ee', // cyan
      '#10b981', // green
      '#f59e0b', // amber
      '#ef4444', // red
      '#ec4899', // pink
      '#06b6d4', // sky
    ];
    const colorMap: Record<string, string> = {};
    speakers.forEach((speaker, index) => {
      colorMap[speaker] = colors[index % colors.length];
    });
    return colorMap;
  }, [speakers]);

  // Filter segments
  const filteredSegments = useMemo(() => {
    return transcript.segments.filter((segment) => {
      const matchesSearch = searchQuery
        ? segment.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
          segment.speaker.toLowerCase().includes(searchQuery.toLowerCase())
        : true;
      const matchesSpeaker = selectedSpeaker
        ? segment.speaker === selectedSpeaker
        : true;
      return matchesSearch && matchesSpeaker;
    });
  }, [transcript.segments, searchQuery, selectedSpeaker]);

  const formatTime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleTimestampClick = (timestamp: string) => {
    // TODO: Implement seeking in recording player
    console.log('Seek to:', timestamp);
  };

  return (
    <div className={styles.container}>
      <div className={styles.controls}>
        <div className={styles.searchWrapper}>
          <Search size={18} className={styles.searchIcon} />
          <Input
            type="text"
            placeholder="Search transcript..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
        </div>

        <div className={styles.filterWrapper}>
          <select
            className={styles.speakerFilter}
            value={selectedSpeaker}
            onChange={(e) => setSelectedSpeaker(e.target.value)}
          >
            <option value="">All Speakers</option>
            {speakers.map((speaker) => (
              <option key={speaker} value={speaker}>
                {speaker}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.legend}>
        {speakers.map((speaker) => (
          <div key={speaker} className={styles.legendItem}>
            <div
              className={styles.colorDot}
              style={{ backgroundColor: speakerColors[speaker] }}
            />
            <span>{speaker}</span>
          </div>
        ))}
      </div>

      <div className={styles.transcript}>
        {filteredSegments.length === 0 ? (
          <div className={styles.empty}>
            <p>No matching transcript segments found.</p>
          </div>
        ) : (
          filteredSegments.map((segment) => (
            <div key={segment.id} className={styles.segment}>
              <div className={styles.segmentHeader}>
                <div
                  className={styles.speaker}
                  style={{ color: speakerColors[segment.speaker] }}
                >
                  <User size={16} />
                  <span className={styles.speakerName}>{segment.speaker}</span>
                </div>
                <button
                  className={styles.timestamp}
                  onClick={() => handleTimestampClick(segment.timestamp)}
                  title="Jump to this point in recording"
                >
                  {formatTime(segment.startTime)}
                </button>
              </div>
              <div className={styles.content}>
                {searchQuery ? (
                  <HighlightedText text={segment.content} highlight={searchQuery} />
                ) : (
                  segment.content
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Helper component to highlight search terms
const HighlightedText: React.FC<{ text: string; highlight: string }> = ({
  text,
  highlight,
}) => {
  if (!highlight.trim()) {
    return <>{text}</>;
  }

  const regex = new RegExp(`(${highlight})`, 'gi');
  const parts = text.split(regex);

  return (
    <>
      {parts.map((part, index) =>
        regex.test(part) ? (
          <mark key={index} className={styles.highlight}>
            {part}
          </mark>
        ) : (
          <span key={index}>{part}</span>
        )
      )}
    </>
  );
};

export default TranscriptViewer;
